import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useMemberAuth } from "../../hooks/useMemberAuth";

/**
 * AccessGrantedPage — the "you're in" moment.
 *
 * Two modes:
 *   • First-time welcome (?welcome=1)  — full-screen cinematic:
 *       "AUTHENTICATING…"
 *       "ACCESS GRANTED"
 *       "MEMBER CLEARANCE VERIFIED"
 *       "YOUR ARCHIVE DESK IS READY"
 *     Auto-forwards to `?next` (defaults to /members) after ~2.8s, OR
 *     tap "CONTINUE INVESTIGATION →" to skip.
 *
 *   • Quiet ack (no ?welcome)         — brief "ACCESS GRANTED" chip
 *     then forward after ~1.0s. Used on repeat sign-ins so daily
 *     members aren't slowed by theatrics.
 *
 * Guard: if the visitor lands here without an active member session,
 * they get bounced to /auth/login instead of a fake "you're in" screen.
 */
const SEQUENCE_MS = [300, 900, 1600, 2300, 3000]; // step boundaries (welcome mode)
const SEQUENCE_TOTAL_MS = 3400;
const QUIET_TOTAL_MS = 1000;

const STEPS = [
  { key: "auth",         line: "AUTHENTICATING…" },
  { key: "grant",        line: "ACCESS GRANTED" },
  { key: "identity",     line: "IDENTITY VERIFIED" },
  { key: "clearance",    line: "CLEARANCE CONFIRMED" },
  { key: "welcome",      line: "WELCOME BACK, INVESTIGATOR" },
];

const AccessGrantedPage = () => {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";
  const nextPath = params.get("next") || "/members";
  const { member, loading, isMember } = useMemberAuth();

  const [step, setStep] = useState(-1);   // -1 = not started
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (loading) return undefined;
    if (!isMember) {
      // No session — punt to login rather than lie about access.
      nav("/auth/login", { replace: true });
      return undefined;
    }
    // Kick off the reveal sequence
    if (welcome) {
      const timers = SEQUENCE_MS.map((ms, i) =>
        setTimeout(() => setStep(i), ms),
      );
      const done = setTimeout(() => setReady(true), SEQUENCE_TOTAL_MS);
      const forward = setTimeout(() => {
        nav(nextPath, { replace: true });
      }, SEQUENCE_TOTAL_MS + 900);
      return () => {
        timers.forEach(clearTimeout);
        clearTimeout(done);
        clearTimeout(forward);
      };
    }
    // Quiet ack path
    setStep(1);
    setReady(true);
    const t = setTimeout(() => nav(nextPath, { replace: true }), QUIET_TOTAL_MS);
    return () => clearTimeout(t);
  }, [loading, isMember, welcome, nextPath, nav]);

  const callsign = member?.callsign || "OPERATIVE";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
           style={{ backgroundColor: "#08070A" }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.35em]"
             style={{ color: "#5a544a" }}>
          Loading…
        </div>
      </div>
    );
  }

  // ---------- QUIET MODE (repeat sign-in) ----------
  if (!welcome) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4"
           style={{ backgroundColor: "#08070A" }} data-testid="access-granted-quiet">
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] mb-3"
               style={{ color: "#5DD4B3" }}>
            // ACCESS GRANTED
          </div>
          <div className="font-display text-2xl sm:text-3xl mb-2"
               style={{ color: "#E4DFD3" }}>
            Welcome back, <span style={{ color: "#E0A526" }}>{callsign}</span>.
          </div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em]"
               style={{ color: "#A39E93" }}>
            Routing to member desk…
          </div>
        </div>
      </div>
    );
  }

  // ---------- WELCOME MODE (first sign-in / signup) ----------
  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4"
         style={{ backgroundColor: "#08070A" }}
         data-testid="access-granted-cinematic">
      {/* Corner crosshairs */}
      <span aria-hidden className="absolute top-4 left-4 w-4 h-4 border-l-2 border-t-2"
            style={{ borderColor: "#D92D20" }} />
      <span aria-hidden className="absolute top-4 right-4 w-4 h-4 border-r-2 border-t-2"
            style={{ borderColor: "#D92D20" }} />
      <span aria-hidden className="absolute bottom-4 left-4 w-4 h-4 border-l-2 border-b-2"
            style={{ borderColor: "#D92D20" }} />
      <span aria-hidden className="absolute bottom-4 right-4 w-4 h-4 border-r-2 border-b-2"
            style={{ borderColor: "#D92D20" }} />

      {/* Faint grid backdrop */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none"
           style={{
             backgroundImage: "linear-gradient(#E4DFD3 1px, transparent 1px), linear-gradient(90deg, #E4DFD3 1px, transparent 1px)",
             backgroundSize: "48px 48px",
           }} />

      <div className="relative text-center max-w-lg w-full">
        <div className="font-mono text-[10px] uppercase tracking-[0.45em] mb-8"
             style={{ color: "#D92D20" }}>
          // THE ARCHIVE · SECURE ENTRY
        </div>

        {/* Sequenced text lines */}
        <div className="min-h-[10.5rem] sm:min-h-[13rem] flex flex-col items-center justify-center gap-3">
          {STEPS.map((s, i) => {
            const shown = step >= i;
            const isActive = step === i && !ready;
            const isPrimary = s.key === "grant";
            return (
              <div
                key={s.key}
                data-testid={`access-granted-step-${s.key}`}
                className="transition-all duration-500 ease-out"
                style={{
                  opacity: shown ? 1 : 0,
                  transform: shown ? "translateY(0)" : "translateY(6px)",
                }}
              >
                <span
                  className={isPrimary
                    ? "font-display text-4xl sm:text-6xl"
                    : "font-mono text-xs sm:text-sm uppercase tracking-[0.35em]"}
                  style={{
                    color: isPrimary
                      ? "#E0A526"
                      : (isActive ? "#5DD4B3" : "#A39E93"),
                    textShadow: isPrimary
                      ? "0 0 24px rgba(224,165,38,0.35)"
                      : "none",
                  }}
                >
                  {s.line}
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em]"
               style={{ color: "#A39E93" }}>
            Callsign · <span style={{ color: "#E0A526" }}>{callsign}</span>
          </div>
          <Link
            to={nextPath}
            data-testid="access-granted-continue"
            className={`inline-flex items-center gap-2 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.35em] transition-opacity duration-500 ${ready ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            style={{ background: "#D92D20", color: "#FFFFFF", border: "1px solid #D92D20" }}
          >
            <ShieldCheck size={12} />
            CONTINUE INVESTIGATION
            <ArrowRight size={12} />
          </Link>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em]"
               style={{ color: "#5a544a" }}>
            Auto-routing to your desk…
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessGrantedPage;
